import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Printer, ArrowLeft, Loader2, FileText } from 'lucide-react';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';

const SelectionLetter = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { workData } = location.state || {};
    const [currentUser, setCurrentUser] = useState(null);
    const [fetchedWorkData, setFetchedWorkData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('currentUser'));
        if (user) {
            setCurrentUser(user);
        }

        if (workData && workData.id) {
            const workNamesRef = ref(db, `ekamwatapzp/WorkNames`);
            onValue(workNamesRef, (snapshot) => {
                if (snapshot.exists()) {
                    const allWorkNames = snapshot.val();
                    const meetingNum = workData.meetingNumber;
                    const workId = workData.id;

                    // Priority 1: Direct lookup
                    let fetchedWork = allWorkNames[meetingNum]?.[workId];

                    // Priority 2: Robust Search
                    if (!fetchedWork) {
                        for (const mNum in allWorkNames) {
                            if (allWorkNames[mNum][workId]) {
                                fetchedWork = allWorkNames[mNum][workId];
                                break;
                            }
                        }
                    }

                    if (fetchedWork) {
                        setFetchedWorkData({
                            ...fetchedWork,
                            id: workId,
                            meetingNumber: meetingNum,
                            schemeName: fetchedWork.schemeName || workData?.schemeName || "योजना उपलब्ध नाही"
                        });
                    } else {
                        setFetchedWorkData(workData);
                    }
                } else {
                    setFetchedWorkData(workData);
                }
                setLoading(false);
            }, (error) => {
                console.error("Error fetching work details:", error);
                setFetchedWorkData(workData);
                setLoading(false);
            }, { onlyOnce: true });
        } else {
            setFetchedWorkData(workData);
            setLoading(false);
        }
    }, [workData]);

    const handlePrint = () => {
        window.print();
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
                <Loader2 className="w-12 h-12 text-[#22165f] animate-spin mb-4" />
                <p className="text-lg font-bold text-gray-700">माहिती लोड होत आहे...</p>
            </div>
        );
    }

    if (!fetchedWorkData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
                <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-4">माहिती उपलब्ध नाही (Data not available)</h2>
                    <p className="mb-6 text-gray-600">कृपया डॅशबोर्डवरून पुन्हा प्रयत्न करा.</p>
                    <button
                        onClick={() => navigate('/subhe-dashboard')}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        डॅशबोर्डवर परत जा
                    </button>
                </div>
            </div>
        );
    }

    const userName = currentUser
        ? `${currentUser.surname || ''} ${currentUser.firstName || ''} ${currentUser.middleName || ''}`.trim()
        : "प्रती,";

    const userAddress = currentUser
        ? `${currentUser.atPo || ''}, ता-${currentUser.taluka || ''}, जि-${currentUser.district || ''}`
        : "";

    const registrationNo = currentUser?.registrationNo || "1342";
    const mobileNo = currentUser?.whatsappNo || "8080039259";
    const date = new Date().toLocaleDateString('mr-IN');
    const meetingNo = fetchedWorkData.meetingNumber || "05/2025-2026";

    return (
        <div className="min-h-screen bg-gray-100 py-10 print:bg-white print:py-0">
            {/* Professional Header - Hidden on Print */}
            <div className="bg-white border-b border-gray-100 shadow-sm px-6 py-4 flex justify-between items-center print:hidden mb-6 sticky top-0 z-50">
                <div className="flex items-center gap-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="group flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-xl hover:bg-gray-100 transition-all font-black text-xs border border-gray-200 active:scale-95 shadow-sm"
                    >
                        <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center border border-gray-200 group-hover:border-blue-200 group-hover:text-blue-600 transition-all">
                            ←
                        </div>
                        MAINTAIN (मागे जा)
                    </button>
                    <div className="h-8 w-[1px] bg-gray-100 hidden md:block"></div>
                    <div className="flex items-center gap-3">
                        <div className="bg-[#22165f] p-2 rounded-lg text-white shadow-lg">
                            <FileText size={20} />
                        </div>
                        <div className="hidden md:block">
                            <h2 className="text-sm font-black text-[#22165f] tracking-tight decoration-yellow-400 underline-offset-4 decoration-2">SELECTION LETTER</h2>
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest leading-none mt-1">निवड पत्र (Official Copy)</p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <div className="text-right mr-4 hidden lg:block">
                        <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{fetchedWorkData?.schemeName || "योजना"}</p>
                        <p className="text-xs font-bold text-[#22165f]">{fetchedWorkData?.workNumber || "कामाचा क्रमांक"}</p>
                    </div>
                    <button
                        onClick={handlePrint}
                        className="flex items-center gap-2 bg-[#22165f] text-white px-6 py-2.5 rounded-xl font-black text-xs shadow-[0_10px_20px_rgba(34,22,95,0.2)] hover:scale-105 transition-all active:scale-95 group uppercase tracking-widest"
                    >
                        <Printer size={16} className="group-hover:animate-bounce" /> प्रिंट करा (Print)
                    </button>
                </div>
            </div>

            {/* Letter Content */}
            <div className="max-w-[900px] mx-auto bg-white p-12 shadow-2xl mt-16 print:mt-0 print:shadow-none print:p-8 min-h-[1100px] relative font-serif text-[#333]">
                {/* Header Section */}
                <div className="flex justify-between items-start mb-8">
                    <div className="border border-gray-400 p-2 px-4 font-bold text-lg">
                        सुशिक्षित बेरोजगार अभियंता
                    </div>
                    <div className="text-right text-sm leading-relaxed font-bold">
                        काम वाटप समिती बैठक क्र. {meetingNo}<br />
                        - 2026<br />
                        बांधकाम विभाग जिल्हा परिषद सांगली<br />
                        दिनांक:- {date}
                    </div>
                </div>

                {/* Recipient Info */}
                <div className="mb-10 text-base leading-relaxed">
                    <p className="font-bold">प्रति,</p>
                    <p className="font-bold">{userName} ,</p>
                    <p>{userAddress}</p>
                    <div className="mt-4">
                        <p>नो.क्र. :- {registrationNo}</p>
                        <p>मो. नं.:- {mobileNo}</p>
                    </div>
                </div>

                {/* Subject & Reference */}
                <div className="text-center mb-10 px-10">
                    <p className="font-bold text-lg border-b-2 border-black inline-block mb-2">
                        विषय :- सुशिक्षित बेरोजगार अभियंता यांना काम वाटपाबाबत.
                    </p>
                    <p className="font-bold mt-2">
                        संदर्भ :- कामवाटप बैठक क्र. {meetingNo.split('/')[0]}, दि. {date}
                    </p>
                </div>

                {/* Body Paragraph 1 */}
                <div className="mb-8 leading-relaxed text-justify indent-12">
                    वरील संदर्भिय पत्रानुसार काम वाटप समितीकडे उपलब्ध झालेल्या कामांच्या यादीमधील नमूद कामापैकी दि. {date} रोजी समिती सभागृह, जिल्हा परिषद सांगली येथे झालेल्या, ऑनलाईन काम वाटप समितीमध्ये आपण मागणी केल्यानुसार समिती पुढे घेतलेल्या निर्णयानुसार खालील काम पूर्ण करण्यासाठी आपणांस देणेत येत आहे.
                </div>

                {/* Work Table */}
                <table className="w-full border-collapse border border-gray-800 mb-8 text-sm">
                    <thead>
                        <tr className="bg-gray-50">
                            <th className="border border-gray-800 p-2 text-left w-20">कामाचा क्रमांक</th>
                            <th className="border border-gray-800 p-2 text-left">कामाचे नांव</th>
                            <th className="border border-gray-800 p-2 text-left w-40">योजनेचे नांव</th>
                            <th className="border border-gray-800 p-2 text-right w-32">अंदाजपत्रकीय किंमत (लाखात)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="text-center">
                            <td className="border border-gray-800 p-3 font-bold">{fetchedWorkData.workNumber || fetchedWorkData.id || "16134"}</td>
                            <td className="border border-gray-800 p-3 marathi-font italic text-sm">{fetchedWorkData.workName || "स्व. ह.भ.प. सावळाराम महाराज प्राथमिक शाळा दुरुस्ती करणे ता. मिरज"}</td>
                            <td className="border border-gray-800 p-3 italic font-bold text-[#22165f]">{fetchedWorkData.schemeName || "जिल्हा परिषद शाळा दुरुस्ती"}</td>
                            <td className="border border-gray-800 p-3 italic">{fetchedWorkData.tenderAmount || "4.50"}</td>
                        </tr>
                    </tbody>
                </table>

                {/* Body Paragraph 2 */}
                <div className="mb-6 leading-relaxed text-justify">
                    तरी आपणास ऑनलाईन कामवाटपामध्ये काम मिळाल्यापासून ७ दिवसाचे आत रु.५००/- चा स्टॅम्प, फॉर्म फी भरून चलन ऑनलाईन अपलोड करून त्याची मूळप्रत या कार्यालयास सादर करून व कार्यारंभ आदेश घेण्याची प्रक्रिया सुरू करावी.
                </div>

                {/* Body Paragraph 3 */}
                <div className="mb-12 leading-relaxed text-justify">
                    ऑनलाईन कामवाटप झाले पासून ७ दिवसात आपण वरील बाबींची पूर्तता न केल्यास आपणांस सदर काम करण्यास स्वास्य नाही असे गृहीत धरून सदरचे काम पुढील कामवाटपामध्ये घेण्यात येईल. त्यानंतर आपले काहीही म्हणणे विचारात घेतले जाणार नाही. तसेच पुढील कामवाटपामध्ये आपणास / आपल्या संस्थेस कोणतेही कामवाटप करणेत येणार नाही याची नोंद घ्यावी.
                </div>

                {/* Footer Signature */}
                <div className="flex justify-end mt-20 relative">
                    <div className="text-center font-bold relative">
                        {/* Fake Signature Image placeholder if needed, or just text */}
                        <div className="mb-8">
                            {/* Transparent signature overlay could go here */}
                            <div className="absolute -top-12 left-1/2 -translate-x-1/2 opacity-20 rotate-12 select-none pointer-events-none">
                                <span className="text-4xl text-blue-800">SIGNED</span>
                            </div>
                        </div>
                        <p>सदस्य सचिव</p>
                        <p>जिल्हा परिषद कामवाटप समिती तथा</p>
                        <p>कार्यकारी अभियंता (इवद), जिल्हा परिषद, सांगली</p>
                    </div>
                </div>

                {/* CC Section */}
                <div className="mt-20 text-sm">
                    <p className="font-bold underline mb-2">प्रत:-</p>
                    <p>1. उप अभियंता (इवद) पं. स., {fetchedWorkData.taluka || "जत"} यांना माहितीसाठी व आवश्यक त्या कार्यवाहीसाठी.</p>
                </div>

                {/* Watermark Background */}
                <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] pointer-events-none select-none z-0">
                    <img
                        src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQe_8y6G8p82X_v0Z1MIn1rN_0P0T4Y-hUq_A&s"
                        alt="ZP Logo"
                        className="w-[500px]"
                    />
                </div>
            </div>
        </div>
    );
};

export default SelectionLetter;
